import { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";

export default function ProjectFinancialDashboardPage({ cabinetId = null }) {
  const [projects, setProjects] = useState([]);
  const [financeEntries, setFinanceEntries] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [schedule, setSchedule] = useState([]);

  useEffect(() => {
    if (cabinetId == null) return;
    loadProjects();
    loadFinance();
    loadMilestones();
    loadSchedule();
  }, [cabinetId]);

  async function loadProjects() {
    const { data, error } = await supabase
      .from("projects")
      .select("*")
      .eq("cabinet_id", cabinetId);

    if (error) {
      console.error("Erreur chargement projets:", error);
    } else {
      setProjects(data || []);
    }
  }

  async function loadFinance() {
    const { data, error } = await supabase
      .from("finance")
      .select("*")
      .eq("cabinet_id", cabinetId);

    if (error) {
      console.error("Erreur chargement finance:", error);
    } else {
      setFinanceEntries(data || []);
    }
  }

  async function loadMilestones() {
    const { data, error } = await supabase
      .from("milestones")
      .select("*")
      .eq("cabinet_id", cabinetId);

    if (error) {
      console.error("Erreur chargement jalons:", error);
    } else {
      setMilestones(data || []);
    }
  }

  async function loadSchedule() {
    const { data, error } = await supabase
      .from("payment_schedule")
      .select("*")
      .eq("cabinet_id", cabinetId);

    if (error) {
      console.error("Erreur chargement échéancier:", error);
    } else {
      setSchedule(data || []);
    }
  }

  function formatMoney(value) {
    return Number(value || 0).toLocaleString("fr-FR") + " MAD";
  }

  function getProjectFinance(projectId) {
    return financeEntries.filter(
      (entry) => String(entry.project_id) === String(projectId)
    );
  }

  function getProjectMilestones(projectId) {
    return milestones.filter(
      (m) => String(m.project_id) === String(projectId)
    );
  }

  function getProjectSchedule(projectId) {
    return schedule.filter(
      (row) => String(row.project_id) === String(projectId)
    );
  }

  function getEncaisseProjet(projectId) {
    return getProjectFinance(projectId)
      .filter((entry) => entry.type === "income")
      .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
  }

  function getDecaisseProjet(projectId) {
    return getProjectFinance(projectId)
      .filter((entry) => entry.type === "expense")
      .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
  }

  function getSoldeProjet(projectId) {
    return getEncaisseProjet(projectId) - getDecaisseProjet(projectId);
  }

  function getProgressProjet(projectId) {
    const list = getProjectMilestones(projectId);
    if (list.length === 0) return 0;
    const done = list.filter((m) => m.status === "done").length;
    return Math.round((done / list.length) * 100);
  }

  function getExpectedAmount(projectBudget, percentage) {
    const budget = Number(projectBudget || 0);
    const pct = Number(percentage || 0);
    if (cabinetId == null) {
    return <div style={{ padding: 24, textAlign: "center", color: "#8B7361" }}>Chargement...</div>;
  }

  return (budget * pct) / 100;
  }

  function getEncaisseInstallment(projectId, installmentNumber) {
    return financeEntries
      .filter(
        (entry) =>
          String(entry.project_id) === String(projectId) &&
          entry.type === "income" &&
          Number(entry.installment_number) === Number(installmentNumber)
      )
      .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
  }

  function getScheduleStats(project) {
    const rows = getProjectSchedule(project.id);

    let conformes = 0;
    let partiels = 0;
    let impayes = 0;

    rows.forEach((row) => {
      const attendu = getExpectedAmount(project.budget, row.percentage);
      const encaisse = getEncaisseInstallment(project.id, row.installment_number);

      if (encaisse >= attendu && attendu > 0) {
        conformes += 1;
      } else if (encaisse > 0 && encaisse < attendu) {
        partiels += 1;
      } else {
        impayes += 1;
      }
    });

    return { conformes, partiels, impayes };
  }

  function getProjectHealth(project) {
    const solde = getSoldeProjet(project.id);
    const progress = getProgressProjet(project.id);
    const stats = getScheduleStats(project);

    if (solde < 0 || stats.impayes >= 2 || progress < 20) {
      return "Bloqué";
    }

    if (stats.partiels > 0 || stats.impayes === 1 || progress < 50) {
      return "À surveiller";
    }

    return "Sain";
  }

  function getStatusLabel(status) {
    if (status === "Sain") return "Actif";
    if (status === "À surveiller") return "En attente";
    return "Bloqué";
  }

  function getStatusBadgeStyle(statusLabel) {
    if (statusLabel === "Actif") {
      return {
        background: "rgba(92,138,92,0.14)",
        color: "#5C8A5C",
        border: "1px solid rgba(92,138,92,0.4)",
      };
    }
    if (statusLabel === "En attente") {
      return {
        background: "rgba(192,154,91,0.14)",
        color: "#C09A5B",
        border: "1px solid rgba(192,154,91,0.4)",
      };
    }
    return {
      background: "rgba(184,92,58,0.14)",
      color: "#B85C3A",
      border: "1px solid rgba(184,92,58,0.4)",
    };
  }

  const budgetTotal = projects.reduce((sum, p) => sum + Number(p.budget || 0), 0);
  const encaisseTotal = projects.reduce((sum, p) => sum + getEncaisseProjet(p.id), 0);
  const decaisseTotal = projects.reduce((sum, p) => sum + getDecaisseProjet(p.id), 0);
  const soldeRestant = budgetTotal - encaisseTotal;
  const avancementJalons = projects.length
    ? Math.round(
        projects.reduce((sum, p) => sum + getProgressProjet(p.id), 0) / projects.length
      )
    : 0;

  const totalImpayes = projects.reduce(
    (sum, p) => sum + getScheduleStats(p).impayes,
    0
  );
  const totalPartiels = projects.reduce(
    (sum, p) => sum + getScheduleStats(p).partiels,
    0
  );
  const ratioEncaisse = budgetTotal > 0 ? Math.min(100, Math.round((encaisseTotal / budgetTotal) * 100)) : 0;

  return (
    <main style={{ padding: 28, background: "#F8F4EE", minHeight: "100vh" }}><style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;700&family=DM+Sans:wght@400;500;700&display=swap');
      `}</style><header style={{ marginBottom: 22 }}><h1 style={{ margin: 0, fontFamily: "'Playfair Display', serif", color: "#3D2E20", fontSize: 34, fontWeight: 700 }}>
          Dashboard Projet Financier
        </h1><p style={{ marginTop: 6, fontFamily: "'DM Sans', sans-serif", color: "#7E6C5B", fontSize: 14 }}>
          Vue synthétique YOMA — lecture rapide des KPIs, alertes et état des projets.
        </p></header><section
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
          gap: 14,
          marginBottom: 18,
        }}
      ><div style={{ background: "#FFFFFF", border: "1px solid #E3D7C8", borderRadius: 12, padding: 16 }}><div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#7E6C5B", marginBottom: 10 }}>Budget total (MAD)</div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 700, color: "#3D2E20", lineHeight: 1.1 }}>
            {formatMoney(budgetTotal)}
          </div></div><div style={{ background: "#FFFFFF", border: "1px solid #D4E7D4", borderRadius: 12, padding: 16 }}><div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#7E6C5B", marginBottom: 10 }}>Encaissé (MAD)</div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 700, color: "#5C8A5C", lineHeight: 1.1 }}>
            {formatMoney(encaisseTotal)}
          </div></div><div style={{ background: "#FFFFFF", border: "1px solid #E3D7C8", borderRadius: 12, padding: 16 }}><div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#7E6C5B", marginBottom: 10 }}>Solde restant (MAD)</div><div
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 28,
              fontWeight: 700,
              color: soldeRestant >= 0 ? "#5C8A5C" : "#B85C3A",
              lineHeight: 1.1,
            }}
          >
            {formatMoney(soldeRestant)}
          </div></div><div style={{ background: "#FFFFFF", border: "1px solid #E3D7C8", borderRadius: 12, padding: 16 }}><div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#7E6C5B", marginBottom: 10 }}>Avancement jalons (%)</div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 700, color: "#3D2E20", marginBottom: 10 }}>
            {avancementJalons}%
          </div><div style={{ width: "100%", height: 10, background: "#EFE4D4", borderRadius: 999 }}><div style={{ width: `${avancementJalons}%`, height: "100%", background: "#C09A5B", borderRadius: 999 }} /></div></div></section><section
        style={{
          display: "grid",
          gridTemplateColumns: "1.5fr 1fr",
          gap: 14,
          marginBottom: 18,
        }}
      ><div style={{ background: "#FFFFFF", border: "1px solid #E3D7C8", borderRadius: 12, padding: 18 }}><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, color: "#3D2E20", marginBottom: 10 }}>
            Synthèse financière
          </div><div style={{ display: "flex", justifyContent: "space-between", fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#7E6C5B", marginBottom: 8 }}><span>Encaissé vs Budget</span><strong style={{ color: "#3D2E20" }}>{ratioEncaisse}%</strong></div><div style={{ width: "100%", height: 12, background: "#EFE4D4", borderRadius: 999, marginBottom: 12 }}><div style={{ width: `${ratioEncaisse}%`, height: "100%", background: "#C09A5B", borderRadius: 999 }} /></div><div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 10 }}><div style={{ background: "#F8F4EE", borderRadius: 10, padding: 10 }}><div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#7E6C5B" }}>Budget</div><div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, color: "#3D2E20" }}>{formatMoney(budgetTotal)}</div></div><div style={{ background: "#F1F8F1", borderRadius: 10, padding: 10 }}><div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#7E6C5B" }}>Encaissé</div><div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, color: "#5C8A5C" }}>{formatMoney(encaisseTotal)}</div></div><div style={{ background: "#F8F4EE", borderRadius: 10, padding: 10 }}><div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#7E6C5B" }}>Décaissé</div><div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, color: "#3D2E20" }}>{formatMoney(decaisseTotal)}</div></div></div></div><div style={{ background: "#FFFFFF", border: "1px solid #E3D7C8", borderRadius: 12, padding: 18 }}><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, color: "#3D2E20", marginBottom: 12 }}>
            Alertes critiques
          </div><div style={{ display: "grid", gap: 10 }}><div style={{ border: "1px solid rgba(184,92,58,0.35)", background: "rgba(184,92,58,0.08)", borderRadius: 10, padding: 12 }}><div style={{ fontFamily: "'DM Sans', sans-serif", color: "#B85C3A", fontSize: 12 }}>Échéances impayées</div><div style={{ fontFamily: "'Playfair Display', serif", color: "#B85C3A", fontWeight: 700, fontSize: 26, lineHeight: 1.1 }}>
                {totalImpayes}
              </div></div><div style={{ border: "1px solid rgba(192,154,91,0.4)", background: "rgba(192,154,91,0.1)", borderRadius: 10, padding: 12 }}><div style={{ fontFamily: "'DM Sans', sans-serif", color: "#C09A5B", fontSize: 12 }}>Échéances en attente</div><div style={{ fontFamily: "'Playfair Display', serif", color: "#C09A5B", fontWeight: 700, fontSize: 26, lineHeight: 1.1 }}>
                {totalPartiels}
              </div></div></div></div></section><section style={{ background: "#FFFFFF", border: "1px solid #E3D7C8", borderRadius: 12, padding: 14 }}><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#3D2E20", marginBottom: 10 }}>
          Portefeuille projets
        </div><table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}><thead><tr style={{ background: "#F5EFE6" }}><th style={{ textAlign: "left", padding: "10px 12px", fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#7E6C5B" }}>Projet</th><th style={{ textAlign: "right", padding: "10px 12px", fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#7E6C5B" }}>Budget</th><th style={{ textAlign: "right", padding: "10px 12px", fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#7E6C5B" }}>Encaissé</th><th style={{ textAlign: "right", padding: "10px 12px", fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#7E6C5B" }}>Solde</th><th style={{ textAlign: "center", padding: "10px 12px", fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#7E6C5B" }}>État</th></tr></thead><tbody>
            {projects.map((project) => {
              const encaisse = getEncaisseProjet(project.id);
              const solde = getSoldeProjet(project.id);
              const status = getStatusLabel(getProjectHealth(project));
              return (
                <tr key={project.id} style={{ borderTop: "1px solid #EFE4D4" }}><td style={{ padding: "11px 12px", fontFamily: "'DM Sans', sans-serif", color: "#3D2E20", fontWeight: 500 }}>
                    {project.name}
                  </td><td style={{ padding: "11px 12px", textAlign: "right", fontFamily: "'DM Sans', sans-serif", color: "#3D2E20" }}>
                    {formatMoney(project.budget)}
                  </td><td style={{ padding: "11px 12px", textAlign: "right", fontFamily: "'DM Sans', sans-serif", color: "#5C8A5C", fontWeight: 700 }}>
                    {formatMoney(encaisse)}
                  </td><td
                    style={{
                      padding: "11px 12px",
                      textAlign: "right",
                      fontFamily: "'DM Sans', sans-serif",
                      color: solde >= 0 ? "#5C8A5C" : "#B85C3A",
                      fontWeight: 700,
                    }}
                  >
                    {formatMoney(solde)}
                  </td><td style={{ padding: "11px 12px", textAlign: "center" }}><span
                      style={{
                        display: "inline-block",
                        padding: "4px 10px",
                        borderRadius: 999,
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        ...getStatusBadgeStyle(status),
                      }}
                    >
                      {status}
                    </span></td></tr>
              );
            })}
          </tbody></table></section></main>
  );
}